import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useParams, useNavigate } from 'react-router-dom';

import { AuthenticatedRoutes, OIDCRedirect } from '../Auth';

import Login from 'components/Login';
import NotFound from 'components/NotFound';
import SessionExpired from 'components/SessionExpired';
import { Loader } from 'components/Loader';

const Home = lazy(() => import('../Home'));
const TableDetails = lazy(() => import('../TableDetails'));
const BaseLayout = lazy(() => import('../BaseLayout'));

const TablesList = lazy(() => import('containers/TablesList'));
const DownloadsPage = lazy(() => import('containers/Downloads/DownloadsPage'));
const Walkthrough = lazy(() => import('containers/Walkthrough'));
const McpTokensPage = lazy(() => import('containers/McpTokens/McpTokensPage'));

const ConversationsPage = lazy(() =>
  import('components/ConversationsPage').then(m => ({
    default: m.ConversationsPage,
  }))
);
const ConversationSearch = lazy(() =>
  import('components/ConversationSearch').then(m => ({
    default: m.ConversationSearch,
  }))
);

const Chatbot =
  process.env.REACT_APP_CHATBOT_ENABLED === 'true'
    ? lazy(() => import('components/Chatbot').then(m => ({ default: m.Chatbot })))
    : null;

function ConversationRedirect() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/chatbot?conversation=${encodeURIComponent(conversationId || '')}`, { replace: true });
  }, [conversationId, navigate]);
  return null;
}

const AppRoutes = () => {
  return (
    <Routes>
      <Route element={<BaseLayout />} path="/">
        <Route element={<AuthenticatedRoutes />} path="/">
          <Route element={<Home />} index />
          <Route
            element={
              <Suspense fallback={<Loader />}>
                <DownloadsPage />
              </Suspense>
            }
            path="/downloads"
          />
          {Chatbot && (
            <Route
              element={
                <Suspense fallback={<Loader />}>
                  <Chatbot />
                </Suspense>
              }
              path="/chatbot"
            />
          )}
          <Route
            element={
              <Suspense fallback={<Loader />}>
                <ConversationsPage />
              </Suspense>
            }
            path="/conversations"
          />
          <Route
            element={
              <Suspense fallback={<Loader />}>
                <ConversationSearch />
              </Suspense>
            }
            path="/conversations/search"
          />
          <Route element={<ConversationRedirect />} path="/conversations/:conversationId" />
          <Route
            element={
              <Suspense fallback={<Loader />}>
                <Walkthrough />
              </Suspense>
            }
            path="/walkthrough"
          />
          <Route
            element={
              <Suspense fallback={<Loader />}>
                <McpTokensPage />
              </Suspense>
            }
            path="/tokens"
          />
          <Route path="/dataSource">
            <Route
              path=":dataSource"
              element={
                <Suspense fallback={<Loader />}>
                  <TablesList />
                </Suspense>
              }
            />
            <Route
              path=":dataSource/database/:databaseName/asset/:tableId/*"
              element={
                <Suspense fallback={<Loader />}>
                  <TableDetails />
                </Suspense>
              }
            />
          </Route>
        </Route>
        <Route element={<NotFound />} path="*" />
        <Route element={<NotFound />} path="/notFound" />
        <Route element={<SessionExpired />} path="/session-expired" />
        <Route element={<Login />} path="/login" />
      </Route>
      <Route element={<OIDCRedirect />} path="/index.html" />
    </Routes>
  );
};

export default AppRoutes;
